import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import Common from "us.common";
import Constants from "us.common/constants";
import { IntlDate, IntlCurrency } from "us.common/functions";
import { IExperian } from "us.collection.debtor/Interfaces";
import { IRootState } from "us.collection/interfaces";
import { PlusOutlined, MinusOutlined } from "us.icons";
import { IOnFilter, IOnSort } from "us.common/components/antd/TableTree";
import { CreditSafeCompany, CreditSafePersonal } from "us.collection.debtor/reducers/Debtor/Interfaces";

const { $Collapse, $Skeleton, $Table, $TableTree, $DateLabel, $AmountLabel } = Common.Components;

interface ISortOrder {
  columnKey: string;
  order: any;
}

type ICreditSafeProvider = {
  debtorCreditInfo: {
    personal: CreditSafePersonal;
    company: CreditSafeCompany;
    isLoading: boolean;
  };
}

const CreditSafeProvider: React.FC<ICreditSafeProvider> = (props) => {
  const { t } = useTranslation(
    ["US.COLLECTION.DEBTOR", "US.COLLECTION.COMMON"],
    { useSuspense: true }
  );
  const {
    debtorCreditInfo,
  } = props;

  const { personal, company, isLoading } = debtorCreditInfo ?? {};

  const [sortedInfo, setSortedInfo] = useState<ISortOrder>({
    columnKey: "",
    order: "",
  });

  // Tax Tabel
  const pesonalColumns: any = [   
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.SCORING"),
      dataIndex: "scoring",
      key: "scoring",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.BUSINESS_OWNER"),
      dataIndex: "businessOwner",
      key: "businessOwner",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.EARNED_INCOME_AND_BUSINESS"),
      dataIndex: "earnedIncomeAndBusiness",
      key: "earnedIncomeAndBusiness",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.earnedIncomeAndBusiness ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.TEXEBLE_INCOME"),
      dataIndex: "texebleIncome",
      key: "texebleIncome",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.texebleIncome ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.TOTAL_INCOME"),
      dataIndex: "totalIncome",
      key: "totalIncome",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.totalIncome ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.FINAL_TAX"),
      dataIndex: "finalTax",
      key: "finalTax",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.finalTax ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.ROP_NUMBER"),
      dataIndex: "ropNumber",
      key: "ropNumber",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.ROP_NUMBER1"),
      dataIndex: "ropNumber1",
      key: "ropNumber1",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.DEBT_NUMBER"),
      dataIndex: "debtNumber",
      key: "debtNumber",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.DEBT_SUM"),
      dataIndex: "debtSum",
      key: "debtSum",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.debtSum ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.DEBT_DATE"),
      dataIndex: "debtDate",
      key: "debtDate",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customFilter: "date",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$DateLabel value={record?.debtDate} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.BANKRUPTCY"),
      dataIndex: "bankruptcy",
      key: "bankruptcy",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.BANKRUPTCY_TYPE"),
      dataIndex: "bankruptcyType",
      key: "bankruptcyType",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.ASSESSED_VALUE_BUILDING"),
      dataIndex: "assessedValueBuilding",
      key: "assessedValueBuilding",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.assessedValueBuilding ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.ASSESSED_VALUE_LAND"),
      dataIndex: "assessedValueLand",
      key: "assessedValueLand",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.assessedValueLand ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.AVERAGE_OWNED_PART_PERCENT"),
      dataIndex: "averageOwnedPartPercent",
      key: "averageOwnedPartPercent",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.ASSESSED_VALUE_OWNED_PART"),
      dataIndex: "assessedValueOwnedPart",
      key: "assessedValueOwnedPart",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
    },
  ];
  

  const companyColumns: any = [
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.COMPANY_TYPE"),
      dataIndex: "companyType",
      key: "companyType",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.COMPANY_TYPE_TEXT"),
      dataIndex: "companyTypeText",
      key: "companyTypeText",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.SCORING"),
      dataIndex: "rating",
      key: "rating",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.RISK_PROGNOSIS"),
      dataIndex: "riskPrognosis",
      key: "riskPrognosis",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.LIMIT"),
      dataIndex: "limit",
      key: "limit",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.limit ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.ASSESSED_VALUE_TOTAL"),
      dataIndex: "assessedValueTotal",
      key: "assessedValueTotal",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.assessedValueTotal ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.ASSESSED_VALUE_BUILDING"),
      dataIndex: "assessedValueBuilding",
      key: "assessedValueBuilding",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.assessedValueBuilding ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.ASSESSED_VALUE_LAND"),
      dataIndex: "assessedValueLand",
      key: "assessedValueLand",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.assessedValueLand ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.AVERAGE_OWNED_PART_PERCENT"),
      dataIndex: "averageOwnedPartPercent",
      key: "averageOwnedPartPercent",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.ASSESSED_VALUE_OWNED_PART"),
      dataIndex: "assessedValueOwnedPart",
      key: "assessedValueOwnedPart",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.assessedValueOwnedPart ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.PAY_DBT"),
      dataIndex: "payDbt",
      key: "payDbt",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.payDbt ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.PAY_INVOICES_AVG_VALUE"),
      dataIndex: "payInvoicesAvgValue",
      key: "payInvoicesAvgValue",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.payInvoicesAvgValue ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.NET_TURNOVER"),
      dataIndex: "netTurnover",
      key: "netTurnover",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.netTurnover ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.NET_PROFIT"),
      dataIndex: "netProfit",
      key: "netProfit",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.netProfit ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.PROFIT_DEPRICIATION"),
      dataIndex: "profitDepriciation",
      key: "profitDepriciation",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.profitDepriciation ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.CURRENT_ASSETS"),
      dataIndex: "currentAssets",
      key: "currentAssets",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.currentAssets ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.FIXT_EQUIP"),
      dataIndex: "fixtEquip",
      key: "fixtEquip",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.fixtEquip ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.SHORT_TERM_LIABS"),
      dataIndex: "shortTermLiabs",
      key: "shortTermLiabs",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.shortTermLiabs ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.LONG_TERM_LIABS"),
      dataIndex: "longTermLiabs",
      key: "longTermLiabs",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: "amount",
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <$AmountLabel value={parseFloat(record?.longTermLiabs ?? "0")} />,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.QUICK_RATIO"),
      dataIndex: "quickRatio",
      key: "quickRatio",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.DEBT_RATIO"),
      dataIndex: "skuldsattningsgrad",
      key: "skuldsattningsgrad",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.ANTAL_ANM"),
      dataIndex: "antalAnm",
      key: "antalAnm",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.ANTAL_ANM_AMAL1"),
      dataIndex: "antalAnmAmal1",
      key: "antalAnmAmal1",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.BANKRUPTCY_TEXT"),
      dataIndex: "bankruptcyText",
      key: "bankruptcyText",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.DEBTOR:CREDITINFO.BANKRUPTCY_CODE"),
      dataIndex: "bankruptcyType",
      key: "bankruptcyType",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customFilter: true,
    },
  ];
  

  // Sort table column
  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    setSortedInfo(sorter);
  };

  const handleSort: IOnSort = (sortData, dataSource) => {
    return sortData(dataSource);
  };

  const handleFilter: IOnFilter = (searchData, dataSource) => {
    return searchData(dataSource);
  };

  return (
    <>
      <$Collapse
        bordered={false}
        defaultActiveKey={["1", "2", "3"]}
        expandIcon={({ isActive }) =>
          isActive ? <MinusOutlined /> : <PlusOutlined />
        }
        className="bui-collapse"
      >
        <$Collapse.Panel
          header={t("US.COLLECTION.DEBTOR:CREDITINFO.GENERALINFO")}
          key="1"
          className="bui-collapse-panel"
        >
          {personal?.entityNo && (
            <div className="d-flex flex-wrap px-2">
              {personal?.identyNo && (
                <div className="info-item">
                  <label>{t("US.COLLECTION.DEBTOR:CREDITINFO.IDENTYNO")} :</label>
                  <span>{personal?.identyNo}</span>
                </div>
              )}
              {personal?.name && (
                <div className="info-item">
                  <label>{t("US.COLLECTION.DEBTOR:CREDITINFO.NAME")} :</label>
                  <span>{personal?.name}</span>
                </div>
              )}
              {personal?.address && (
                <div className="info-item">
                  <label>{t("US.COLLECTION.DEBTOR:CREDITINFO.ADDRESS")} :</label>
                  <span>{personal?.address}</span>
                </div>
              )}
              {personal?.postalCode && (
                <div className="info-item">
                  <label>{t("US.COLLECTION.DEBTOR:CREDITINFO.POSTALCODE")} :</label>
                  <span>{personal?.postalCode}</span>
                </div>
              )}
              {personal?.washProvider && (
                <div className="info-item">
                  <label>{t("US.COLLECTION.DEBTOR:CREDITINFO.WASH_PROVIDER")} :</label>
                  <span>{personal?.washProvider}</span>
                </div>
              )}
            </div>
          )}
          {company?.entityNo && (
            <div className="d-flex flex-wrap px-2">
              {company?.identyNo && (
                <div className="info-item">
                  <label>{t("US.COLLECTION.DEBTOR:CREDITINFO.IDENTYNO")} :</label>
                  <span>{company?.identyNo}</span>
                </div>
              )}
              {company?.name && (
                <div className="info-item">
                  <label>{t("US.COLLECTION.DEBTOR:CREDITINFO.NAME")} :</label>
                  <span>{company?.name}</span>
                </div>
              )}
              {company?.address && (
                <div className="info-item">
                  <label>{t("US.COLLECTION.DEBTOR:CREDITINFO.ADDRESS")} :</label>
                  <span>{company?.address}</span>
                </div>
              )}
              {company?.postalCode && (
                <div className="info-item">
                  <label>{t("US.COLLECTION.DEBTOR:CREDITINFO.POSTALCODE")} :</label>
                  <span>{company?.postalCode}</span>
                </div>
              )}
              {company?.washProvider && (
                <div className="info-item">
                  <label>{t("US.COLLECTION.DEBTOR:CREDITINFO.WASH_PROVIDER")} :</label>
                  <span>{company?.washProvider}</span>
                </div>
              )}
            </div>
          )}
        </$Collapse.Panel>
          <div>
            <$Skeleton loading={isLoading} active paragraph={{ rows: 2 }}>
              <$TableTree
                rowKey="taxYear"
                data={company?.entityNo ? [company] : [personal]}
                columns={company?.entityNo ? companyColumns : pesonalColumns}
                size="small"
                className=""
                onSort={handleSort}
                onFilter={handleFilter}
                filterOnType={true}
                resetOnSourceChange={true}
                bordered
                pagination={false}
                scroll={{ x: 1200 }}
                firstColSkipFilterProps={-1}
              />
            </$Skeleton>
          </div>
      </$Collapse>
    </>
  );
};

const mapStateToProps = (state: IRootState) => {
  const { common } = state;
  return {
    currentDateFormat: common.currentDateFormat,
    currentCurrency: common.currentCurrency,
    currentLanguage: common.currentLanguage,
  };
};

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(CreditSafeProvider);
